import { PythonTopicExercise } from '../../../../types/python-topic';

export const introduccionExercises: PythonTopicExercise[] = [
  {
    id: 1,
    title: 'Variables y tipos básicos',
    description: 'Aprende a crear variables y entender los tipos de datos básicos en Python: enteros, flotantes, booleanos y strings.',
    initialCode: `# Crea variables de diferentes tipos
# Entero
edad = 

# Flotante
altura = 

# Booleano
es_mayor = 

# String
nombre = 

# Imprime todas las variables
print(edad)
print(altura)
print(es_mayor)
print(nombre)`,
    solution: `# Crea variables de diferentes tipos
# Entero
edad = 25

# Flotante
altura = 1.75

# Booleano
es_mayor = True

# String
nombre = "Juan"

# Imprime todas las variables
print(edad)
print(altura)
print(es_mayor)
print(nombre)`,
    testCases: [],
    hints: [
      'Los enteros son números sin decimales',
      'Los flotantes tienen punto decimal',
      'Los booleanos son True o False',
      'Los strings van entre comillas'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué aprender variables y tipos básicos primero?',
      content: `Las **variables** son la base de todo programa. Son contenedores que guardan información que tu programa necesita para funcionar. Sin variables, no podrías almacenar datos, hacer cálculos, o tomar decisiones.

### ¿Por qué es importante entender tipos?
- **Enteros (int)**: Para contar cosas, índices, cantidades enteras
- **Flotantes (float)**: Para medidas, cálculos científicos, porcentajes
- **Booleanos (bool)**: Para decisiones lógicas (sí/no, verdadero/falso)
- **Strings (str)**: Para texto, nombres, mensajes

### En el mundo real:
Cuando un desarrollador crea un sistema de facturación, necesita variables para almacenar el nombre del cliente (string), la cantidad de productos (int), el precio (float), y si el cliente tiene descuento (bool). Sin variables, sería imposible manejar esta información.`,
      keyPoints: [
        'Las variables son nombres para valores',
        'Python deduce el tipo automáticamente (tipado dinámico)',
        'Cada tipo tiene operaciones específicas que puedes hacer',
        'Los nombres de variables deben ser descriptivos'
      ],
      realWorldExample: 'En un sistema de e-commerce, necesitas variables para el nombre del producto (string), stock disponible (int), precio (float), y si está en oferta (bool).'
    },
    theory: `# Variables y Tipos Básicos en Python

## ¿Qué es una Variable?

Una **variable** es un nombre que le das a un valor para poder usarlo más tarde en tu programa. Es como una caja con una etiqueta donde guardas información.

\`\`\`python
nombre = "María"
edad = 30
altura = 1.65
es_estudiante = True
\`\`\`

## Tipos de Datos Básicos

Python tiene varios tipos de datos fundamentales:

### 1. Enteros (int)
Números enteros, positivos o negativos:

\`\`\`python
edad = 25
cantidad = -10
poblacion = 1000000
\`\`\`

### 2. Flotantes (float)
Números con decimales:

\`\`\`python
altura = 1.75
precio = 99.99
temperatura = -5.5
\`\`\`

### 3. Booleanos (bool)
Solo dos valores: \`True\` o \`False\`:

\`\`\`python
es_mayor = True
tiene_descuento = False
esta_activo = True
\`\`\`

### 4. Strings (str)
Texto entre comillas:

\`\`\`python
nombre = "Juan"
mensaje = 'Hola mundo'
direccion = "Calle 123"
\`\`\`

## Asignación de Variables

Para asignar un valor a una variable, usas el operador \`=\`:

\`\`\`python
variable = valor
\`\`\`

**Importante**: El valor del lado derecho se asigna a la variable del lado izquierdo. Nunca al revés.

## Tipado Dinámico

Python deduce automáticamente el tipo de una variable según el valor que le asignas:

\`\`\`python
x = 5        # x es un int
x = 5.0      # Ahora x es un float
x = "cinco"  # Ahora x es un string
\`\`\`

Esto es diferente a otros lenguajes donde debes declarar el tipo explícitamente.

## Nombres de Variables

Reglas para nombrar variables:
- Pueden contener letras, números y guión bajo (\`_\`)
- **NO** pueden empezar con número
- Python distingue mayúsculas de minúsculas
- No pueden ser palabras reservadas (\`if\`, \`while\`, \`for\`, etc.)

**Ejemplos válidos**:
\`\`\`python
nombre = "Juan"
edad_usuario = 25
_total = 100
\`\`\`

**Ejemplos inválidos**:
\`\`\`python
2nombre = "Juan"  # Error: no puede empezar con número
nombre-usuario = "Juan"  # Error: no puede tener guión
if = 5  # Error: if es palabra reservada
\`\`\`

## Buenas Prácticas

1. **Nombres descriptivos**: \`edad\` es mejor que \`e\`
2. **Usa snake_case**: \`nombre_usuario\` en lugar de \`nombreUsuario\`
3. **Evita nombres muy cortos**: \`x\`, \`y\` solo para casos muy simples
4. **Sé consistente**: Si usas \`nombre_cliente\`, no uses \`clienteNombre\` en otro lugar`,
    tips: [
      'Usa nombres descriptivos para tus variables',
      'Python no requiere declarar el tipo, lo deduce automáticamente',
      'Puedes cambiar el tipo de una variable reasignándola',
      'Los nombres de variables son case-sensitive (mayúsculas/minúsculas importan)',
      'Usa snake_case (palabras_separadas_con_guion_bajo) para nombres de variables'
    ],
    advice: [
      'Elige nombres que describan claramente qué contiene la variable',
      'Evita nombres genéricos como "data" o "temp"',
      'Si una variable representa una cantidad, incluye la unidad en el nombre si es necesario',
      'No uses palabras reservadas de Python como nombres de variables'
    ]
  },
  {
    id: 2,
    title: 'Operadores y expresiones',
    description: 'Aprende a usar operadores aritméticos, de comparación y lógicos para crear expresiones en Python.',
    initialCode: `# Operadores aritméticos
a = 10
b = 3

# Suma
suma = 

# Resta
resta = 

# Multiplicación
multiplicacion = 

# División
division = 

# División entera
division_entera = 

# Módulo (resto)
modulo = 

# Potencia
potencia = 

# Imprime los resultados
print("Suma:", suma)
print("Resta:", resta)
print("Multiplicación:", multiplicacion)
print("División:", division)
print("División entera:", division_entera)
print("Módulo:", modulo)
print("Potencia:", potencia)`,
    solution: `# Operadores aritméticos
a = 10
b = 3

# Suma
suma = a + b

# Resta
resta = a - b

# Multiplicación
multiplicacion = a * b

# División
division = a / b

# División entera
division_entera = a // b

# Módulo (resto)
modulo = a % b

# Potencia
potencia = a ** b

# Imprime los resultados
print("Suma:", suma)
print("Resta:", resta)
print("Multiplicación:", multiplicacion)
print("División:", division)
print("División entera:", division_entera)
print("Módulo:", modulo)
print("Potencia:", potencia)`,
    testCases: [],
    hints: [
      'Usa + para sumar',
      'Usa - para restar',
      'Usa * para multiplicar',
      'Usa / para dividir',
      'Usa // para división entera',
      'Usa % para obtener el resto',
      'Usa ** para potencia'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué aprender operadores y expresiones?',
      content: `Los **operadores** son símbolos que realizan operaciones sobre valores. Son la forma en que le dices a Python qué hacer con los datos.

### ¿Por qué son fundamentales?
- **Operaciones matemáticas**: Sin operadores no podrías hacer cálculos
- **Comparaciones**: Necesitas comparar valores para tomar decisiones
- **Lógica**: Los operadores lógicos te permiten combinar condiciones
- **Expresiones complejas**: Puedes combinar múltiples operadores

### En el mundo real:
Cuando un desarrollador calcula el precio total de una compra, usa operadores: \`precio_total = cantidad * precio_unitario + impuestos\`. Sin operadores, no podría hacer estos cálculos automáticos.`,
      keyPoints: [
        'Los operadores aritméticos realizan cálculos matemáticos',
        'Los operadores de comparación devuelven True o False',
        'Los operadores lógicos combinan condiciones booleanas',
        'Puedes combinar múltiples operadores en una expresión'
      ],
      realWorldExample: 'En un sistema de facturación, necesitas operadores para calcular subtotales (multiplicación), aplicar descuentos (resta), calcular impuestos (multiplicación), y sumar todo (suma).'
    },
    theory: `# Operadores y Expresiones en Python

## Operadores Aritméticos

Estos operadores realizan operaciones matemáticas:

| Operador | Descripción | Ejemplo | Resultado |
|----------|-------------|---------|-----------|
| \`+\` | Suma | \`5 + 3\` | \`8\` |
| \`-\` | Resta | \`5 - 3\` | \`2\` |
| \`*\` | Multiplicación | \`5 * 3\` | \`15\` |
| \`/\` | División | \`5 / 3\` | \`1.666...\` |
| \`//\` | División entera | \`5 // 3\` | \`1\` |
| \`%\` | Módulo (resto) | \`5 % 3\` | \`2\` |
| \`**\` | Potencia | \`5 ** 3\` | \`125\` |

### Ejemplos:

\`\`\`python
a = 10
b = 3

suma = a + b          # 13
resta = a - b         # 7
multiplicacion = a * b  # 30
division = a / b      # 3.333...
division_entera = a // b  # 3
modulo = a % b        # 1 (resto de 10/3)
potencia = a ** b     # 1000
\`\`\`

## Operadores de Comparación

Estos operadores comparan dos valores y devuelven \`True\` o \`False\`:

| Operador | Descripción | Ejemplo | Resultado |
|----------|-------------|---------|-----------|
| \`==\` | Igual a | \`5 == 3\` | \`False\` |
| \`!=\` | Diferente de | \`5 != 3\` | \`True\` |
| \`<\` | Menor que | \`5 < 3\` | \`False\` |
| \`>\` | Mayor que | \`5 > 3\` | \`True\` |
| \`<=\` | Menor o igual | \`5 <= 3\` | \`False\` |
| \`>=\` | Mayor o igual | \`5 >= 3\` | \`True\` |

**Importante**: \`==\` compara valores, \`=\` asigna valores. Son diferentes.

### Ejemplos:

\`\`\`python
edad = 18

es_mayor = edad >= 18  # True
es_menor = edad < 18   # False
es_exactamente = edad == 18  # True
\`\`\`

## Operadores Lógicos

Estos operadores combinan condiciones booleanas:

| Operador | Descripción | Ejemplo | Resultado |
|----------|-------------|---------|-----------|
| \`and\` | Y lógico | \`True and False\` | \`False\` |
| \`or\` | O lógico | \`True or False\` | \`True\` |
| \`not\` | Negación | \`not True\` | \`False\` |

### Ejemplos:

\`\`\`python
edad = 18
tiene_licencia = True

puede_conducir = edad >= 18 and tiene_licencia  # True
es_joven = edad < 30 or edad > 65  # True
no_puede = not puede_conducir  # False
\`\`\`

## Precedencia de Operadores

Python evalúa operadores en un orden específico:

1. Paréntesis: \`()\`
2. Potencia: \`**\`
3. Multiplicación, División, Módulo: \`*\`, \`/\`, \`//\`, \`%\`
4. Suma, Resta: \`+\`, \`-\`
5. Comparación: \`<\`, \`>\`, \`==\`, etc.
6. Lógicos: \`not\`, \`and\`, \`or\`

### Ejemplo:

\`\`\`python
resultado = 2 + 3 * 4  # 14 (no 20)
# Primero: 3 * 4 = 12
# Luego: 2 + 12 = 14

resultado = (2 + 3) * 4  # 20
# Primero: (2 + 3) = 5
# Luego: 5 * 4 = 20
\`\`\`

## Expresiones Complejas

Puedes combinar múltiples operadores:

\`\`\`python
# Calcular el área de un círculo
radio = 5
area = 3.14159 * radio ** 2

# Verificar si un número está en un rango
numero = 15
esta_en_rango = numero >= 10 and numero <= 20
\`\`\``,
    tips: [
      'Usa paréntesis para clarificar el orden de operaciones',
      '== compara valores, = asigna valores',
      'and requiere que ambas condiciones sean True',
      'or requiere que al menos una condición sea True',
      'not invierte el valor booleano',
      '// devuelve la división entera (sin decimales)',
      '% devuelve el resto de una división'
    ],
    advice: [
      'Usa paréntesis cuando tengas dudas sobre el orden de evaluación',
      'Sé consistente con espacios alrededor de operadores',
      'Para comparaciones complejas, considera dividirlas en múltiples variables',
      'Recuerda que == es para comparar, = es para asignar'
    ]
  },
  {
    id: 3,
    title: 'Print y salida',
    description: 'Aprende a usar la función print() para mostrar información en pantalla y formatear la salida.',
    initialCode: `# Usa print() para mostrar mensajes
# Imprime un mensaje simple
print()

# Imprime múltiples valores separados por espacios
nombre = "María"
edad = 25
print()

# Usa el parámetro end para cambiar cómo termina la línea
print("Hola", end="")
print(" mundo")

# Imprime en múltiples líneas
print()
print("Segunda línea")`,
    solution: `# Usa print() para mostrar mensajes
# Imprime un mensaje simple
print("Hola mundo")

# Imprime múltiples valores separados por espacios
nombre = "María"
edad = 25
print("Nombre:", nombre, "Edad:", edad)

# Usa el parámetro end para cambiar cómo termina la línea
print("Hola", end=" ")
print("mundo")

# Imprime en múltiples líneas
print("Primera línea")
print("Segunda línea")`,
    testCases: [],
    hints: [
      'print() muestra texto en pantalla',
      'Puedes pasar múltiples valores separados por comas',
      'end="" evita el salto de línea',
      'Cada print() normalmente termina con un salto de línea'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué aprender print() primero?',
      content: `La función **print()** es tu forma de comunicarte con el usuario. Sin ella, tu programa podría hacer cálculos perfectos, pero nadie sabría qué está pasando.

### ¿Por qué es fundamental?
- **Feedback**: Muestra resultados de tus cálculos
- **Debugging**: Te ayuda a entender qué está pasando en tu código
- **Interacción**: Es la forma básica de mostrar información al usuario
- **Verificación**: Puedes verificar que tu código funciona correctamente

### En el mundo real:
Cuando un desarrollador crea una calculadora, usa print() para mostrar el resultado. Sin print(), el usuario nunca vería la respuesta, aunque el cálculo sea correcto.`,
      keyPoints: [
        'print() muestra información en pantalla',
        'Puedes imprimir texto, variables o expresiones',
        'Múltiples valores se separan automáticamente con espacios',
        'El parámetro end controla cómo termina la línea'
      ],
      realWorldExample: 'En un sistema de facturación, print() muestra el total a pagar, los productos comprados, y el cambio. Sin print(), el usuario nunca vería esta información.'
    },
    theory: `# Print y Salida en Python

## La Función print()

La función \`print()\` es la forma más básica de mostrar información en Python. Imprime texto, variables o expresiones en la pantalla.

### Sintaxis Básica

\`\`\`python
print("Hola mundo")
\`\`\`

Esto imprime: \`Hola mundo\`

## Imprimir Variables

Puedes imprimir el valor de una variable:

\`\`\`python
nombre = "Juan"
print(nombre)  # Imprime: Juan
\`\`\`

## Múltiples Valores

Puedes pasar múltiples valores a \`print()\`. Se separan automáticamente con espacios:

\`\`\`python
nombre = "María"
edad = 25
print("Nombre:", nombre, "Edad:", edad)
# Imprime: Nombre: María Edad: 25
\`\`\`

## El Parámetro end

Por defecto, \`print()\` termina con un salto de línea (\`\\n\`). Puedes cambiarlo con el parámetro \`end\`:

\`\`\`python
print("Hola", end=" ")
print("mundo")
# Imprime: Hola mundo (en una sola línea)

print("Hola", end="***")
print("mundo")
# Imprime: Hola***mundo
\`\`\`

## Ejemplos Comunes

### Imprimir texto simple:
\`\`\`python
print("Bienvenido")
\`\`\`

### Imprimir variables:
\`\`\`python
total = 100
print("Total:", total)
\`\`\`

### Imprimir expresiones:
\`\`\`python
a = 5
b = 3
print("Suma:", a + b)
\`\`\`

### Múltiples líneas:
\`\`\`python
print("Línea 1")
print("Línea 2")
print("Línea 3")
\`\`\`

### Sin salto de línea:
\`\`\`python
print("Cargando", end="")
print("...", end="")
print(" Completado")
# Imprime: Cargando... Completado
\`\`\``,
    tips: [
      'print() siempre agrega un salto de línea al final (a menos que uses end)',
      'Puedes imprimir texto, números, variables o expresiones',
      'Múltiples valores se separan automáticamente con espacios',
      'Usa end="" para evitar el salto de línea',
      'Puedes combinar texto y variables en un solo print()'
    ],
    advice: [
      'Usa print() frecuentemente para ver qué está pasando en tu código',
      'Imprime valores intermedios cuando estés aprendiendo',
      'Usa mensajes descriptivos: print("Total:", total) es mejor que print(total)',
      'El parámetro end es útil para crear salidas personalizadas'
    ]
  },
  {
    id: 4,
    title: 'Condicionales (if, else, elif)',
    description: 'Aprende a usar condicionales para que tu programa tome decisiones basadas en condiciones.',
    initialCode: `# Condicionales básicos
edad = 18

# Si la edad es mayor o igual a 18, imprime "Mayor de edad"
if edad >= 18:
    print()

# Agrega un else para el caso contrario
edad = 15
if edad >= 18:
    print("Mayor de edad")
else:
    print()

# Usa elif para múltiples condiciones
nota = 85
if nota >= 90:
    print("Excelente")
elif nota >= 70:
    print()
elif nota >= 60:
    print("Aprobado")
else:
    print("Reprobado")`,
    solution: `# Condicionales básicos
edad = 18

# Si la edad es mayor o igual a 18, imprime "Mayor de edad"
if edad >= 18:
    print("Mayor de edad")

# Agrega un else para el caso contrario
edad = 15
if edad >= 18:
    print("Mayor de edad")
else:
    print("Menor de edad")

# Usa elif para múltiples condiciones
nota = 85
if nota >= 90:
    print("Excelente")
elif nota >= 70:
    print("Bueno")
elif nota >= 60:
    print("Aprobado")
else:
    print("Reprobado")`,
    testCases: [],
    hints: [
      'if ejecuta código solo si la condición es True',
      'else ejecuta código cuando la condición es False',
      'elif verifica otra condición si la anterior fue False',
      'La indentación es crucial en Python',
      'Usa : después de if, elif y else'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué aprender condicionales?',
      content: `Los **condicionales** permiten que tu programa tome decisiones. Sin ellos, tu programa siempre haría lo mismo, sin importar la situación.

### ¿Por qué son esenciales?
- **Decisiones**: Tu programa puede responder diferente según las circunstancias
- **Validación**: Puedes verificar si los datos son válidos antes de usarlos
- **Flujo de control**: Determinas qué código se ejecuta y cuándo
- **Lógica de negocio**: Implementas reglas como "si el usuario es mayor de edad, mostrar contenido"

### En el mundo real:
Cuando un sistema de e-commerce calcula el envío, usa condicionales: si el pedido es mayor a $100, envío gratis; si no, cobra $10. Sin condicionales, no podría aplicar estas reglas.`,
      keyPoints: [
        'if ejecuta código solo si la condición es True',
        'else maneja el caso cuando la condición es False',
        'elif permite múltiples condiciones en secuencia',
        'La indentación define qué código pertenece a cada bloque'
      ],
      realWorldExample: 'En un sistema de autenticación, si el usuario ingresa la contraseña correcta, se le permite acceso; si no, se muestra un error. Esto requiere condicionales.'
    },
    theory: `# Condicionales en Python

## ¿Qué son los Condicionales?

Los **condicionales** permiten que tu programa ejecute código diferente dependiendo de si una condición es verdadera o falsa.

## Sintaxis Básica: if

La estructura más simple es \`if\`:

\`\`\`python
if condición:
    # Código que se ejecuta si la condición es True
    print("La condición es verdadera")
\`\`\`

**Importante**: 
- La condición debe terminar con \`:\`
- El código indentado se ejecuta solo si la condición es \`True\`
- La indentación es obligatoria (normalmente 4 espacios)

## Ejemplo Simple

\`\`\`python
edad = 18

if edad >= 18:
    print("Eres mayor de edad")
\`\`\`

## if-else

Usa \`else\` para manejar el caso cuando la condición es \`False\`:

\`\`\`python
edad = 15

if edad >= 18:
    print("Eres mayor de edad")
else:
    print("Eres menor de edad")
\`\`\`

## if-elif-else

Usa \`elif\` (else-if) para múltiples condiciones:

\`\`\`python
nota = 85

if nota >= 90:
    print("Excelente")
elif nota >= 70:
    print("Bueno")
elif nota >= 60:
    print("Aprobado")
else:
    print("Reprobado")
\`\`\`

**Importante**: Python evalúa las condiciones en orden. Solo ejecuta el código del primer bloque cuya condición sea \`True\`.

## Condiciones Booleanas

Las condiciones pueden ser:
- Comparaciones: \`edad >= 18\`, \`nombre == "Juan"\`
- Variables booleanas: \`es_activo\`
- Expresiones lógicas: \`edad >= 18 and tiene_licencia\`

### Ejemplos:

\`\`\`python
# Comparación numérica
if edad >= 18:
    print("Mayor")

# Comparación de strings
if nombre == "María":
    print("Hola María")

# Variable booleana
if es_activo:
    print("Usuario activo")

# Expresión lógica
if edad >= 18 and tiene_licencia:
    print("Puede conducir")
\`\`\`

## Indentación

La indentación es **crucial** en Python. Define qué código pertenece a cada bloque:

\`\`\`python
if edad >= 18:
    print("Eres mayor")  # Este código está dentro del if
    print("Puedes votar")  # Este también
print("Fin del programa")  # Este NO está dentro del if
\`\`\`

**Reglas**:
- Usa 4 espacios por nivel de indentación
- Sé consistente: no mezcles espacios y tabs
- Todo el código dentro de un bloque debe tener la misma indentación

## Condiciones Anidadas

Puedes poner condicionales dentro de otros condicionales:

\`\`\`python
edad = 20
tiene_licencia = True

if edad >= 18:
    if tiene_licencia:
        print("Puede conducir")
    else:
        print("Necesita licencia")
else:
    print("Es menor de edad")
\`\`\``,
    tips: [
      'Siempre termina las condiciones con :',
      'La indentación define qué código pertenece al bloque',
      'elif verifica otra condición solo si las anteriores fueron False',
      'Puedes tener múltiples elif pero solo un else',
      'Usa and/or para combinar condiciones',
      'Las condiciones se evalúan de arriba hacia abajo'
    ],
    advice: [
      'Usa nombres descriptivos para variables en condiciones',
      'Mantén las condiciones simples y legibles',
      'Si tienes muchas condiciones, considera usar elif en lugar de múltiples if',
      'Siempre prueba ambos casos (True y False) cuando sea posible',
      'La indentación correcta es esencial: usa 4 espacios consistentemente'
    ]
  },
  {
    id: 5,
    title: 'Ciclos (while y for)',
    description: 'Aprende a usar ciclos para repetir código múltiples veces de forma eficiente.',
    initialCode: `# Ciclo while
# Imprime los números del 1 al 5
contador = 1
while contador <= 5:
    print()
    contador = contador + 1

# Ciclo for con range
# Imprime los números del 0 al 4
for i in range(5):
    print()

# Ciclo for con range específico
# Imprime los números del 1 al 5
for i in range():
    print(i)`,
    solution: `# Ciclo while
# Imprime los números del 1 al 5
contador = 1
while contador <= 5:
    print(contador)
    contador = contador + 1

# Ciclo for con range
# Imprime los números del 0 al 4
for i in range(5):
    print(i)

# Ciclo for con range específico
# Imprime los números del 1 al 5
for i in range(1, 6):
    print(i)`,
    testCases: [],
    hints: [
      'while repite mientras la condición sea True',
      'for itera sobre una secuencia',
      'range(5) genera números del 0 al 4',
      'range(1, 6) genera números del 1 al 5',
      'No olvides actualizar el contador en while para evitar bucles infinitos'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué aprender ciclos?',
      content: `Los **ciclos** (bucles) te permiten repetir código sin tener que escribirlo múltiples veces. Son fundamentales para procesar datos, iterar sobre listas, y automatizar tareas repetitivas.

### ¿Por qué son esenciales?
- **Repetición eficiente**: Ejecuta el mismo código múltiples veces
- **Procesamiento de datos**: Itera sobre listas, strings, etc.
- **Automatización**: Realiza tareas repetitivas automáticamente
- **Escalabilidad**: Funciona con 10 o 10,000 elementos igual de bien

### En el mundo real:
Cuando un sistema procesa una lista de 1000 pedidos, usa un ciclo para calcular el total de cada uno. Sin ciclos, tendrías que escribir el código 1000 veces.`,
      keyPoints: [
        'while repite mientras la condición sea True',
        'for itera sobre una secuencia (lista, string, range, etc.)',
        'range() genera una secuencia de números',
        'Es crucial actualizar variables en while para evitar bucles infinitos'
      ],
      realWorldExample: 'En un sistema de facturación, un ciclo procesa cada producto en el carrito, calcula subtotales, y suma todo. Sin ciclos, esto sería imposible de hacer eficientemente.'
    },
    theory: `# Ciclos en Python

## ¿Qué son los Ciclos?

Los **ciclos** (bucles) permiten ejecutar un bloque de código múltiples veces. Hay dos tipos principales: \`while\` y \`for\`.

## Ciclo while

El ciclo \`while\` repite código **mientras** una condición sea verdadera:

\`\`\`python
contador = 1
while contador <= 5:
    print(contador)
    contador = contador + 1
\`\`\`

Esto imprime: \`1 2 3 4 5\`

**Importante**: Debes actualizar la variable de condición dentro del ciclo, o el ciclo nunca terminará (bucle infinito).

## Ciclo for

El ciclo \`for\` itera sobre una secuencia (lista, string, range, etc.):

\`\`\`python
for i in range(5):
    print(i)
\`\`\`

Esto imprime: \`0 1 2 3 4\`

## La Función range()

\`range()\` genera una secuencia de números:

- \`range(5)\`: Genera \`0, 1, 2, 3, 4\` (5 números empezando en 0)
- \`range(1, 6)\`: Genera \`1, 2, 3, 4, 5\` (del 1 al 5)
- \`range(0, 10, 2)\`: Genera \`0, 2, 4, 6, 8\` (de 0 a 9, de 2 en 2)

### Ejemplos:

\`\`\`python
# Números del 0 al 4
for i in range(5):
    print(i)

# Números del 1 al 5
for i in range(1, 6):
    print(i)

# Números pares del 0 al 8
for i in range(0, 10, 2):
    print(i)
\`\`\`

## Comparación: while vs for

### Cuándo usar while:
- Cuando no sabes cuántas veces necesitas repetir
- Cuando la condición depende de algo que cambia durante la ejecución
- Para bucles que pueden no ejecutarse nunca

\`\`\`python
# Ejemplo: seguir pidiendo entrada hasta que sea válida
numero = -1
while numero < 0:
    numero = int(input("Ingresa un número positivo: "))
\`\`\`

### Cuándo usar for:
- Cuando sabes cuántas veces necesitas repetir
- Cuando iteras sobre una secuencia conocida
- Para procesar cada elemento de una lista

\`\`\`python
# Ejemplo: procesar cada elemento de una lista
frutas = ["manzana", "banana", "naranja"]
for fruta in frutas:
    print(fruta)
\`\`\`

## Acumulación en Ciclos

Es común usar ciclos para acumular valores:

\`\`\`python
# Sumar números del 1 al 10
suma = 0
for i in range(1, 11):
    suma = suma + i
print("Suma:", suma)  # 55
\`\`\`

## Bucles Anidados

Puedes poner ciclos dentro de otros ciclos:

\`\`\`python
# Tabla de multiplicar del 1 al 3
for i in range(1, 4):
    for j in range(1, 4):
        print(f"{i} x {j} = {i * j}")
\`\`\`

## Control de Flujo en Ciclos

### break: Salir del ciclo
\`\`\`python
for i in range(10):
    if i == 5:
        break  # Sale del ciclo cuando i es 5
    print(i)
# Imprime: 0 1 2 3 4
\`\`\`

### continue: Saltar a la siguiente iteración
\`\`\`python
for i in range(10):
    if i % 2 == 0:
        continue  # Salta números pares
    print(i)
# Imprime: 1 3 5 7 9
\`\`\``,
    tips: [
      'while repite mientras la condición sea True',
      'for itera sobre una secuencia',
      'range(n) genera números del 0 al n-1',
      'range(inicio, fin) genera números del inicio al fin-1',
      'Siempre actualiza la variable en while para evitar bucles infinitos',
      'for es más común cuando iteras sobre secuencias conocidas'
    ],
    advice: [
      'Usa for cuando sepas cuántas veces necesitas repetir',
      'Usa while cuando la condición depende de algo que cambia',
      'Siempre verifica que tu ciclo while tenga una forma de terminar',
      'range() es muy útil para generar secuencias de números',
      'Considera usar break o continue para controlar el flujo del ciclo'
    ]
  },
  {
    id: 6,
    title: 'Números y operaciones matemáticas',
    description: 'Aprende a trabajar con números enteros y flotantes, y realizar operaciones matemáticas en Python.',
    initialCode: `# Trabaja con números
# Enteros
a = 10
b = 3

# División normal (da float)
division = 

# División entera (da int)
division_entera = 

# Módulo (resto)
resto = 

# Potencia
potencia = 

# Flotantes
pi = 3.14159
radio = 5

# Calcula el área de un círculo (pi * radio^2)
area = 

print("División:", division)
print("División entera:", division_entera)
print("Resto:", resto)
print("Potencia:", potencia)
print("Área del círculo:", area)`,
    solution: `# Trabaja con números
# Enteros
a = 10
b = 3

# División normal (da float)
division = a / b

# División entera (da int)
division_entera = a // b

# Módulo (resto)
resto = a % b

# Potencia
potencia = a ** b

# Flotantes
pi = 3.14159
radio = 5

# Calcula el área de un círculo (pi * radio^2)
area = pi * radio ** 2

print("División:", division)
print("División entera:", division_entera)
print("Resto:", resto)
print("Potencia:", potencia)
print("Área del círculo:", area)`,
    testCases: [],
    hints: [
      '/ hace división normal (siempre da float)',
      '// hace división entera (da int)',
      '% devuelve el resto de una división',
      '** es para potencia',
      'Los flotantes pueden tener imprecisiones en cálculos'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué entender números y operaciones matemáticas?',
      content: `Los **números** son fundamentales en programación. Casi todos los programas necesitan hacer cálculos, contar cosas, o procesar datos numéricos.

### ¿Por qué es importante?
- **Cálculos**: Necesitas hacer operaciones matemáticas en casi cualquier programa
- **Diferencia int/float**: Entender cuándo usar cada tipo evita errores
- **Operaciones especiales**: // y % son muy útiles en programación
- **Precisión**: Los flotantes tienen limitaciones que debes conocer

### En el mundo real:
Cuando un sistema calcula el precio total de una compra, necesita sumar precios (flotantes), contar productos (enteros), calcular descuentos porcentuales, y redondear resultados. Todo esto requiere entender números y operaciones.`,
      keyPoints: [
        'Los enteros (int) son números sin decimales',
        'Los flotantes (float) son números con decimales',
        '/ siempre da float, // da int',
        '% es útil para verificar divisibilidad',
        'Los flotantes pueden tener pequeñas imprecisiones'
      ],
      realWorldExample: 'En un sistema de facturación, necesitas enteros para contar productos, flotantes para precios, y operaciones para calcular totales, impuestos y descuentos.'
    },
    theory: `# Números y Operaciones Matemáticas en Python

## Tipos de Números

Python tiene dos tipos principales de números:

### Enteros (int)
Números sin decimales:

\`\`\`python
edad = 25
cantidad = -10
poblacion = 1000000
\`\`\`

### Flotantes (float)
Números con decimales:

\`\`\`python
precio = 99.99
temperatura = -5.5
pi = 3.14159
\`\`\`

## Operaciones Aritméticas

### División Normal (/)
Siempre devuelve un float:

\`\`\`python
10 / 3  # 3.3333333333333335
5 / 2   # 2.5
\`\`\`

### División Entera (//)
Devuelve solo la parte entera (int):

\`\`\`python
10 // 3  # 3
5 // 2   # 2
\`\`\`

### Módulo (%)
Devuelve el resto de una división:

\`\`\`python
10 % 3  # 1 (resto de 10/3)
5 % 2   # 1 (resto de 5/2)
\`\`\`

**Uso común**: Verificar si un número es par o impar:
\`\`\`python
numero % 2 == 0  # True si es par
\`\`\`

### Potencia (**)
Eleva un número a una potencia:

\`\`\`python
2 ** 3   # 8 (2 al cubo)
5 ** 2   # 25 (5 al cuadrado)
10 ** 3  # 1000
\`\`\`

## Conversión entre Tipos

Puedes convertir entre tipos:

\`\`\`python
# Convertir float a int
int(3.7)  # 3 (trunca, no redondea)

# Convertir int a float
float(5)  # 5.0

# Convertir string a número
int("42")    # 42
float("3.14")  # 3.14
\`\`\`

## Precisión de Flotantes

Los flotantes pueden tener pequeñas imprecisiones:

\`\`\`python
0.1 + 0.2  # 0.30000000000000004 (no exactamente 0.3)
\`\`\`

Esto es normal y se debe a cómo las computadoras almacenan números decimales.

## Funciones Matemáticas Útiles

\`\`\`python
# Valor absoluto
abs(-5)  # 5

# Redondear
round(3.7)  # 4
round(3.14159, 2)  # 3.14 (2 decimales)

# Máximo y mínimo
max(1, 5, 3)  # 5
min(1, 5, 3)  # 1
\`\`\`

## Ejemplos Prácticos

### Calcular área de un círculo:
\`\`\`python
pi = 3.14159
radio = 5
area = pi * radio ** 2
print(area)  # 78.53975
\`\`\`

### Verificar si un número es par:
\`\`\`python
numero = 8
if numero % 2 == 0:
    print("Es par")
\`\`\`

### Contar dígitos:
\`\`\`python
numero = 12345
cantidad_digitos = len(str(numero))  # 5
\`\`\``,
    tips: [
      '/ siempre da float, incluso si divides enteros',
      '// trunca hacia abajo (no redondea)',
      '% es muy útil para verificar divisibilidad',
      '** es más eficiente que multiplicar múltiples veces',
      'Los flotantes pueden tener pequeñas imprecisiones (es normal)',
      'Usa round() cuando necesites redondear'
    ],
    advice: [
      'Usa int para contar cosas (cantidad de productos, usuarios, etc.)',
      'Usa float para medidas y precios',
      '// es útil cuando necesitas división entera (como en algoritmos)',
      '% es excelente para verificar si un número es par/impar o múltiplo',
      'Ten cuidado con la precisión de flotantes en cálculos financieros críticos'
    ]
  },
  {
    id: 7,
    title: 'Strings (Cadenas de texto)',
    description: 'Aprende a trabajar con texto: crear strings, acceder a caracteres, usar métodos comunes y formatear texto.',
    initialCode: `# Trabaja con strings
texto = "Hola Mundo"

# Accede al primer carácter
primer_caracter = 

# Accede al último carácter
ultimo_caracter = 

# Obtén una subcadena (del índice 0 al 4)
subcadena = 

# Convierte a mayúsculas
mayusculas = 

# Convierte a minúsculas
minusculas = 

# Reemplaza texto
nuevo_texto = texto.replace()

# Divide el string en palabras
palabras = texto.split()

print("Primer carácter:", primer_caracter)
print("Último carácter:", ultimo_caracter)
print("Subcadena:", subcadena)
print("Mayúsculas:", mayusculas)
print("Minúsculas:", minusculas)
print("Reemplazado:", nuevo_texto)
print("Palabras:", palabras)`,
    solution: `# Trabaja con strings
texto = "Hola Mundo"

# Accede al primer carácter
primer_caracter = texto[0]

# Accede al último carácter
ultimo_caracter = texto[-1]

# Obtén una subcadena (del índice 0 al 4)
subcadena = texto[0:4]

# Convierte a mayúsculas
mayusculas = texto.upper()

# Convierte a minúsculas
minusculas = texto.lower()

# Reemplaza texto
nuevo_texto = texto.replace("Mundo", "Python")

# Divide el string en palabras
palabras = texto.split()

print("Primer carácter:", primer_caracter)
print("Último carácter:", ultimo_caracter)
print("Subcadena:", subcadena)
print("Mayúsculas:", mayusculas)
print("Minúsculas:", minusculas)
print("Reemplazado:", nuevo_texto)
print("Palabras:", palabras)`,
    testCases: [],
    hints: [
      'Los índices empiezan en 0',
      'texto[-1] accede al último carácter',
      'texto[inicio:fin] obtiene una subcadena',
      'Los strings son inmutables (no se pueden modificar directamente)',
      'Los métodos como upper() devuelven un nuevo string'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué aprender strings?',
      content: `Los **strings** (cadenas de texto) son fundamentales en programación. Casi todos los programas necesitan trabajar con texto: nombres de usuarios, mensajes, datos de entrada, etc.

### ¿Por qué son esenciales?
- **Datos de texto**: Nombres, direcciones, mensajes, etc.
- **Procesamiento**: Necesitas manipular y transformar texto
- **Entrada/Salida**: Los usuarios interactúan con texto
- **Datos estructurados**: Muchos datos vienen como texto (CSV, JSON, etc.)

### En el mundo real:
Cuando un sistema procesa formularios, necesita validar nombres (strings), convertir a mayúsculas para consistencia, extraer información de direcciones de email, y formatear mensajes para mostrar al usuario. Todo esto requiere trabajar con strings.`,
      keyPoints: [
        'Los strings son secuencias de caracteres',
        'Puedes acceder a caracteres individuales con índices',
        'Los strings son inmutables (no se pueden modificar)',
        'Los métodos devuelven nuevos strings',
        'Puedes usar slicing para obtener subcadenas'
      ],
      realWorldExample: 'En un sistema de usuarios, necesitas strings para nombres, emails, validar formatos, convertir a mayúsculas/minúsculas, y formatear mensajes personalizados.'
    },
    theory: `# Strings (Cadenas de Texto) en Python

## ¿Qué son los Strings?

Los **strings** son secuencias de caracteres. Representan texto en Python.

\`\`\`python
nombre = "Juan"
mensaje = 'Hola mundo'
direccion = "Calle 123"
\`\`\`

## Creación de Strings

Puedes usar comillas simples o dobles:

\`\`\`python
texto1 = "Hola"
texto2 = 'Mundo'
# Ambos son equivalentes
\`\`\`

## Acceso a Caracteres

Los strings son secuencias, puedes acceder a caracteres individuales:

\`\`\`python
texto = "Hola"
primer = texto[0]   # 'H'
segundo = texto[1]  # 'o'
ultimo = texto[-1]  # 'a' (índice negativo cuenta desde el final)
\`\`\`

## Slicing (Rebanado)

Puedes obtener subcadenas usando slicing:

\`\`\`python
texto = "Hola Mundo"
inicio = texto[0:4]    # "Hola" (del índice 0 al 3)
final = texto[5:]      # "Mundo" (del índice 5 al final)
medio = texto[2:6]     # "la M" (del índice 2 al 5)
\`\`\`

**Nota**: El índice final no se incluye.

## Operaciones con Strings

### Concatenación (+)
\`\`\`python
nombre = "Juan"
saludo = "Hola " + nombre  # "Hola Juan"
\`\`\`

### Repetición (*)
\`\`\`python
texto = "Hola"
repetido = texto * 3  # "HolaHolaHola"
\`\`\`

### Longitud (len)
\`\`\`python
texto = "Python"
longitud = len(texto)  # 6
\`\`\`

### Pertenencia (in)
\`\`\`python
texto = "Python"
"Py" in texto   # True
"Java" in texto  # False
\`\`\`

## Métodos Comunes de Strings

### Conversión de Mayúsculas/Minúsculas
\`\`\`python
texto = "Hola Mundo"
mayusculas = texto.upper()   # "HOLA MUNDO"
minusculas = texto.lower()   # "hola mundo"
\`\`\`

### Reemplazo
\`\`\`python
texto = "Hola Mundo"
nuevo = texto.replace("Mundo", "Python")  # "Hola Python"
\`\`\`

### División
\`\`\`python
texto = "manzana,banana,naranja"
frutas = texto.split(",")  # ["manzana", "banana", "naranja"]
\`\`\`

### Unión
\`\`\`python
frutas = ["manzana", "banana", "naranja"]
texto = ",".join(frutas)  # "manzana,banana,naranja"
\`\`\`

### Eliminar Espacios
\`\`\`python
texto = "  Hola Mundo  "
limpio = texto.strip()  # "Hola Mundo"
\`\`\`

## Inmutabilidad

Los strings son **inmutables**: no puedes modificar un string existente.

\`\`\`python
texto = "Hola"
texto[0] = "h"  # Error! No se puede modificar
\`\`\`

En su lugar, creas un nuevo string:

\`\`\`python
texto = "Hola"
nuevo = "h" + texto[1:]  # "hola" (nuevo string)
\`\`\`

## f-Strings (Formateo)

Las f-strings permiten insertar variables en strings:

\`\`\`python
nombre = "Juan"
edad = 25
mensaje = f"Hola, soy {nombre} y tengo {edad} años"
# "Hola, soy Juan y tengo 25 años"
\`\`\`

## Ejemplos Prácticos

### Validar formato de email:
\`\`\`python
email = "usuario@ejemplo.com"
if "@" in email and "." in email:
    print("Email válido")
\`\`\`

### Capitalizar nombre:
\`\`\`python
nombre = "juan pérez"
capitalizado = nombre.title()  # "Juan Pérez"
\`\`\`

### Contar palabras:
\`\`\`python
texto = "Hola mundo Python"
palabras = texto.split()
cantidad = len(palabras)  # 3
\`\`\``,
    tips: [
      'Los índices empiezan en 0',
      'texto[-1] accede al último carácter',
      'texto[inicio:fin] obtiene subcadena (fin no incluido)',
      'Los strings son inmutables: los métodos devuelven nuevos strings',
      'Usa f-strings para formatear texto con variables',
      'split() divide por espacios por defecto'
    ],
    advice: [
      'Usa métodos de string en lugar de escribir código manual',
      'Los strings son inmutables: siempre crean nuevos strings',
      'f-strings son la forma moderna de formatear texto',
      'Ten cuidado con espacios al inicio/final: usa strip()',
      'split() es muy útil para procesar datos separados por delimitadores'
    ]
  },
  {
    id: 8,
    title: 'Listas básicas',
    description: 'Aprende a crear listas, acceder a elementos, agregar/eliminar elementos y usar métodos comunes de listas.',
    initialCode: `# Trabaja con listas
# Crea una lista de frutas
frutas = ["manzana", "banana", "naranja"]

# Accede al primer elemento
primera = 

# Accede al último elemento
ultima = 

# Agrega un elemento al final
frutas.append()

# Inserta un elemento en la posición 1
frutas.insert()

# Elimina "banana"
frutas.remove()

# Obtén la longitud de la lista
longitud = len()

# Verifica si "manzana" está en la lista
esta_presente = "manzana" in 

print("Primera fruta:", primera)
print("Última fruta:", ultima)
print("Lista completa:", frutas)
print("Longitud:", longitud)
print("¿Está manzana?:", esta_presente)`,
    solution: `# Trabaja con listas
# Crea una lista de frutas
frutas = ["manzana", "banana", "naranja"]

# Accede al primer elemento
primera = frutas[0]

# Accede al último elemento
ultima = frutas[-1]

# Agrega un elemento al final
frutas.append("uva")

# Inserta un elemento en la posición 1
frutas.insert(1, "pera")

# Elimina "banana"
frutas.remove("banana")

# Obtén la longitud de la lista
longitud = len(frutas)

# Verifica si "manzana" está en la lista
esta_presente = "manzana" in frutas

print("Primera fruta:", primera)
print("Última fruta:", ultima)
print("Lista completa:", frutas)
print("Longitud:", longitud)
print("¿Está manzana?:", esta_presente)`,
    testCases: [],
    hints: [
      'Los índices empiezan en 0',
      'lista[-1] accede al último elemento',
      'append() agrega al final',
      'insert(posicion, elemento) inserta en una posición',
      'remove(elemento) elimina por valor',
      'len() obtiene la longitud'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué aprender listas?',
      content: `Las **listas** son estructuras de datos fundamentales que te permiten almacenar múltiples valores en una sola variable. Son esenciales para manejar colecciones de datos.

### ¿Por qué son esenciales?
- **Colecciones de datos**: Almacenan múltiples valores relacionados
- **Iteración**: Puedes procesar cada elemento con un ciclo
- **Flexibilidad**: Pueden crecer o reducirse dinámicamente
- **Organización**: Agrupan datos relacionados de forma lógica

### En el mundo real:
Cuando un sistema de carrito de compras almacena productos seleccionados, usa una lista. Puedes agregar productos, eliminar productos, contar cuántos hay, y procesar cada uno para calcular el total. Sin listas, esto sería muy difícil.`,
      keyPoints: [
        'Las listas almacenan múltiples valores',
        'Puedes acceder a elementos por índice',
        'Las listas son mutables (puedes modificarlas)',
        'append() agrega al final, insert() en una posición',
        'remove() elimina por valor, del elimina por índice'
      ],
      realWorldExample: 'En un sistema de gestión de tareas, una lista almacena todas las tareas pendientes. Puedes agregar nuevas tareas, marcar como completadas (eliminar), y procesar cada una para mostrarlas.'
    },
    theory: `# Listas Básicas en Python

## ¿Qué son las Listas?

Las **listas** son colecciones ordenadas de elementos. Pueden contener cualquier tipo de dato y pueden modificarse.

\`\`\`python
frutas = ["manzana", "banana", "naranja"]
numeros = [1, 2, 3, 4, 5]
mixta = ["texto", 42, 3.14, True]
\`\`\`

## Creación de Listas

### Lista vacía:
\`\`\`python
lista_vacia = []
\`\`\`

### Lista con elementos:
\`\`\`python
frutas = ["manzana", "banana", "naranja"]
\`\`\`

## Acceso a Elementos

Los elementos se acceden por índice (empezando en 0):

\`\`\`python
frutas = ["manzana", "banana", "naranja"]
primera = frutas[0]   # "manzana"
segunda = frutas[1]   # "banana"
ultima = frutas[-1]   # "naranja" (índice negativo)
\`\`\`

## Modificar Elementos

Las listas son **mutables**: puedes cambiar sus elementos:

\`\`\`python
frutas = ["manzana", "banana", "naranja"]
frutas[1] = "pera"  # Cambia "banana" por "pera"
\`\`\`

## Agregar Elementos

### append(): Agrega al final
\`\`\`python
frutas = ["manzana", "banana"]
frutas.append("naranja")  # ["manzana", "banana", "naranja"]
\`\`\`

### insert(): Inserta en una posición
\`\`\`python
frutas = ["manzana", "banana"]
frutas.insert(1, "pera")  # ["manzana", "pera", "banana"]
\`\`\`

## Eliminar Elementos

### remove(): Elimina por valor
\`\`\`python
frutas = ["manzana", "banana", "naranja"]
frutas.remove("banana")  # ["manzana", "naranja"]
\`\`\`

### del: Elimina por índice
\`\`\`python
frutas = ["manzana", "banana", "naranja"]
del frutas[1]  # ["manzana", "naranja"]
\`\`\`

## Operaciones con Listas

### Longitud (len)
\`\`\`python
frutas = ["manzana", "banana", "naranja"]
longitud = len(frutas)  # 3
\`\`\`

### Pertenencia (in)
\`\`\`python
frutas = ["manzana", "banana", "naranja"]
"manzana" in frutas  # True
"uva" in frutas       # False
\`\`\`

### Concatenación (+)
\`\`\`python
lista1 = [1, 2, 3]
lista2 = [4, 5, 6]
combinada = lista1 + lista2  # [1, 2, 3, 4, 5, 6]
\`\`\`

### Repetición (*)
\`\`\`python
lista = [1, 2]
repetida = lista * 3  # [1, 2, 1, 2, 1, 2]
\`\`\`

## Slicing (Rebanado)

Puedes obtener sublistas:

\`\`\`python
frutas = ["manzana", "banana", "naranja", "uva"]
primeras = frutas[0:2]   # ["manzana", "banana"]
ultimas = frutas[2:]     # ["naranja", "uva"]
\`\`\`

## Iterar sobre Listas

### Con for:
\`\`\`python
frutas = ["manzana", "banana", "naranja"]
for fruta in frutas:
    print(fruta)
\`\`\`

### Con índices:
\`\`\`python
frutas = ["manzana", "banana", "naranja"]
for i in range(len(frutas)):
    print(f"{i}: {frutas[i]}")
\`\`\`

## Métodos Útiles

### count(): Cuenta apariciones
\`\`\`python
numeros = [1, 2, 2, 3, 2]
cantidad = numeros.count(2)  # 3
\`\`\`

### index(): Encuentra posición
\`\`\`python
frutas = ["manzana", "banana", "naranja"]
posicion = frutas.index("banana")  # 1
\`\`\`

### sort(): Ordena la lista
\`\`\`python
numeros = [3, 1, 4, 1, 5]
numeros.sort()  # [1, 1, 3, 4, 5]
\`\`\`

## Listas vs Strings

| Característica | Listas | Strings |
|----------------|--------|---------|
| Mutabilidad | Mutables | Inmutables |
| Contenido | Cualquier tipo | Solo caracteres |
| Modificación | Puedes cambiar elementos | Debes crear nuevo string |

## Ejemplos Prácticos

### Procesar una lista de precios:
\`\`\`python
precios = [10.5, 20.0, 15.75]
total = sum(precios)  # 46.25
\`\`\`

### Filtrar elementos:
\`\`\`python
numeros = [1, 2, 3, 4, 5, 6]
pares = [n for n in numeros if n % 2 == 0]  # [2, 4, 6]
\`\`\`

### Encontrar máximo/mínimo:
\`\`\`python
numeros = [5, 2, 8, 1, 9]
maximo = max(numeros)  # 9
minimo = min(numeros)  # 1
\`\`\``,
    tips: [
      'Los índices empiezan en 0',
      'lista[-1] accede al último elemento',
      'Las listas son mutables: puedes modificarlas',
      'append() agrega al final, insert() en una posición específica',
      'remove() elimina por valor, del elimina por índice',
      'len() obtiene la cantidad de elementos',
      'in verifica si un elemento está en la lista'
    ],
    advice: [
      'Usa listas para almacenar colecciones de datos relacionados',
      'append() es más común que insert() para agregar elementos',
      'Ten cuidado al eliminar elementos: remove() elimina la primera aparición',
      'Usa len() antes de acceder a índices para evitar errores',
      'Las listas pueden contener cualquier tipo de dato, incluso otras listas'
    ]
  },
  // Ejercicios adicionales para Variables y tipos básicos
  {
    id: 9,
    title: 'Variables y tipos básicos - Ejercicio 2',
    description: 'Crea variables para representar información de un producto: nombre, precio, cantidad en stock y si está disponible.',
    initialCode: `# Crea variables para un producto
# Nombre del producto (string)
nombre_producto = 

# Precio del producto (float)
precio = 

# Cantidad en stock (int)
stock = 

# ¿Está disponible? (bool)
disponible = 

# Imprime toda la información
print("Producto:", nombre_producto)
print("Precio: $", precio)
print("Stock:", stock)
print("Disponible:", disponible)`,
    solution: `# Crea variables para un producto
# Nombre del producto (string)
nombre_producto = "Laptop"

# Precio del producto (float)
precio = 999.99

# Cantidad en stock (int)
stock = 15

# ¿Está disponible? (bool)
disponible = True

# Imprime toda la información
print("Producto:", nombre_producto)
print("Precio: $", precio)
print("Stock:", stock)
print("Disponible:", disponible)`,
    testCases: [],
    hints: [
      'Usa comillas para strings',
      'Los precios suelen ser float',
      'El stock es un número entero',
      'disponible es True o False'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué practicar con diferentes tipos de variables?',
      content: `Practicar con diferentes tipos de variables te ayuda a entender cuándo usar cada tipo. En aplicaciones reales, necesitas combinar múltiples tipos de datos para representar información completa.

### ¿Por qué este ejercicio?
- **Contexto real**: Representa un producto real con múltiples atributos
- **Tipos mixtos**: Combina string, float, int y bool en un solo contexto
- **Aplicación práctica**: Es exactamente lo que harías en un sistema de inventario

### En el mundo real:
Cuando un desarrollador crea un sistema de e-commerce, necesita variables de diferentes tipos para cada producto: nombre (string), precio (float), stock (int), y estado (bool). Este ejercicio replica esa situación.`,
      keyPoints: [
        'Los productos necesitan múltiples tipos de datos',
        'Cada tipo tiene un propósito específico',
        'Combinar tipos es común en aplicaciones reales',
        'Los nombres descriptivos ayudan a entender qué representa cada variable'
      ],
      realWorldExample: 'En un sistema de inventario, cada producto tiene nombre (string), precio (float), cantidad (int), y estado activo/inactivo (bool).'
    },
    theory: `# Combinando Diferentes Tipos de Variables

## Variables en Contexto Real

En aplicaciones reales, rara vez trabajas con un solo tipo de variable. Normalmente combinas múltiples tipos para representar información completa.

## Ejemplo: Información de Producto

Un producto necesita varios tipos de datos:

\`\`\`python
# String: Nombre descriptivo
nombre_producto = "Laptop"

# Float: Precio con decimales
precio = 999.99

# Int: Cantidad entera
stock = 15

# Bool: Estado binario
disponible = True
\`\`\`

## Por qué Cada Tipo

### String para nombres
- Permite texto descriptivo
- Fácil de leer y entender
- Puede contener espacios y caracteres especiales

### Float para precios
- Permite decimales (necesario para moneda)
- Precisión para cálculos financieros
- Estándar en sistemas de facturación

### Int para cantidades
- Números enteros (no puedes tener 15.5 productos)
- Más eficiente que float para contar
- Natural para operaciones de stock

### Bool para estados
- Dos estados claros: True/False
- Perfecto para flags y estados binarios
- Fácil de usar en condicionales

## Buenas Prácticas

1. **Nombres descriptivos**: \`nombre_producto\` es mejor que \`n\`
2. **Tipos apropiados**: Usa float para precios, int para cantidades
3. **Consistencia**: Si usas \`precio\` en un lugar, úsalo igual en otros
4. **Agrupación lógica**: Variables relacionadas deben tener nombres relacionados`,
    tips: [
      'Elige el tipo correcto según el dato que representas',
      'Los precios casi siempre son float',
      'Las cantidades suelen ser int',
      'Los estados binarios son bool',
      'Los nombres descriptivos son strings'
    ],
    advice: [
      'Piensa en qué tipo de dato necesita cada información',
      'Practica creando variables para diferentes contextos (productos, usuarios, eventos)',
      'Observa cómo se combinan tipos en aplicaciones reales',
      'Los nombres descriptivos hacen el código más legible'
    ]
  },
  {
    id: 10,
    title: 'Variables y tipos básicos - Ejercicio 3',
    description: 'Crea variables para información personal: nombre completo, edad, altura, y si es estudiante.',
    initialCode: `# Información personal
# Tu nombre completo (string)
nombre_completo = 

# Tu edad (int)
edad = 

# Tu altura en metros (float)
altura = 

# ¿Eres estudiante? (bool)
es_estudiante = 

# Muestra la información
print("Nombre:", nombre_completo)
print("Edad:", edad, "años")
print("Altura:", altura, "metros")
print("Estudiante:", es_estudiante)`,
    solution: `# Información personal
# Tu nombre completo (string)
nombre_completo = "María García"

# Tu edad (int)
edad = 22

# Tu altura en metros (float)
altura = 1.65

# ¿Eres estudiante? (bool)
es_estudiante = True

# Muestra la información
print("Nombre:", nombre_completo)
print("Edad:", edad, "años")
print("Altura:", altura, "metros")
print("Estudiante:", es_estudiante)`,
    testCases: [],
    hints: [
      'Los nombres completos pueden tener espacios',
      'La edad es un número entero',
      'La altura tiene decimales',
      'es_estudiante es True o False'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué practicar con información personal?',
      content: `Practicar con información personal te ayuda a entender cómo representar datos de usuarios en aplicaciones. Casi todas las aplicaciones necesitan almacenar información de personas.

### ¿Por qué este ejercicio?
- **Datos de usuario**: Es lo que almacenas en sistemas de usuarios
- **Tipos variados**: Combina string, int, float y bool
- **Aplicación común**: Formularios, perfiles, bases de datos

### En el mundo real:
Cuando te registras en cualquier aplicación (red social, e-commerce, banco), ingresas información similar: nombre (string), edad (int), y respuestas sí/no (bool). Este ejercicio replica ese proceso.`,
      keyPoints: [
        'La información personal requiere múltiples tipos',
        'Los nombres pueden tener espacios (string)',
        'Las edades son números enteros',
        'Las medidas tienen decimales (float)',
        'Las preguntas sí/no son booleanas'
      ],
      realWorldExample: 'En un sistema de registro de usuarios, necesitas almacenar nombre (string), edad (int), altura opcional (float), y si es estudiante (bool) para descuentos.'
    },
    theory: `# Variables para Información Personal

## Representar Datos de Usuario

Cuando trabajas con información de usuarios, necesitas diferentes tipos de variables para diferentes tipos de datos.

## Ejemplo Completo

\`\`\`python
nombre_completo = "María García"  # String: puede tener espacios
edad = 22                          # Int: años son enteros
altura = 1.65                      # Float: medidas tienen decimales
es_estudiante = True               # Bool: respuesta sí/no
\`\`\`

## Consideraciones por Tipo

### Strings para nombres
- Pueden contener espacios
- Pueden tener caracteres especiales (acentos, guiones)
- No tienen límite de longitud práctica

### Int para edades
- Siempre números enteros (no puedes tener 22.5 años)
- No pueden ser negativos (en contextos normales)
- Tienen límites razonables (0-150 años)

### Float para medidas
- Necesitan precisión decimal
- Altura, peso, temperatura requieren decimales
- Pueden tener muchas cifras decimales según necesidad

### Bool para estados
- Perfecto para preguntas sí/no
- Fácil de usar en condicionales
- Claridad semántica

## Validación de Datos

En aplicaciones reales, validarías estos datos:

\`\`\`python
# Verificar que la edad sea razonable
if edad < 0 or edad > 150:
    print("Edad inválida")

# Verificar que la altura sea positiva
if altura <= 0:
    print("Altura inválida")
\`\`\`

## Formateo de Salida

Puedes formatear la salida para que sea más legible:

\`\`\`python
print(f"Nombre: {nombre_completo}")
print(f"Edad: {edad} años")
print(f"Altura: {altura:.2f} metros")  # 2 decimales
print(f"Estudiante: {'Sí' if es_estudiante else 'No'}")
\`\`\``,
    tips: [
      'Los nombres pueden tener espacios y caracteres especiales',
      'Las edades son siempre enteros',
      'Las medidas físicas suelen ser float',
      'Los estados binarios son bool',
      'Puedes formatear la salida para mejor presentación'
    ],
    advice: [
      'Piensa en qué tipo de dato representa mejor cada información',
      'Considera validaciones para datos de entrada',
      'Practica formatear la salida de manera legible',
      'Observa cómo las aplicaciones reales manejan información personal'
    ]
  },
  // Ejercicios adicionales para Operadores y expresiones
  {
    id: 11,
    title: 'Operadores y expresiones - Ejercicio 2',
    description: 'Calcula el área y perímetro de un rectángulo usando operadores aritméticos.',
    initialCode: `# Calcula área y perímetro de un rectángulo
base = 10
altura = 5

# Área = base * altura
area = 

# Perímetro = 2 * (base + altura)
perimetro = 

print("Área:", area)
print("Perímetro:", perimetro)`,
    solution: `# Calcula área y perímetro de un rectángulo
base = 10
altura = 5

# Área = base * altura
area = base * altura

# Perímetro = 2 * (base + altura)
perimetro = 2 * (base + altura)

print("Área:", area)
print("Perímetro:", perimetro)`,
    testCases: [],
    hints: [
      'Usa * para multiplicar',
      'Usa + para sumar',
      'Los paréntesis cambian el orden de operaciones',
      'El perímetro es la suma de todos los lados'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar operadores para cálculos geométricos?',
      content: `Los operadores aritméticos son fundamentales para cálculos geométricos y matemáticos. Aprender a combinarlos correctamente te permite resolver problemas reales.

### ¿Por qué este ejercicio?
- **Aplicación práctica**: Cálculos geométricos son comunes en programación
- **Orden de operaciones**: Practicas el uso de paréntesis
- **Expresiones complejas**: Combina múltiples operadores

### En el mundo real:
Cuando un sistema calcula el área de una habitación para cotizar pisos, o el perímetro para calcular cercas, usa exactamente estos operadores. Sin operadores, no podrías hacer estos cálculos automáticos.`,
      keyPoints: [
        'Los paréntesis controlan el orden de operaciones',
        'Puedes combinar múltiples operadores en una expresión',
        'Las fórmulas matemáticas se traducen directamente a código',
        'Multiplicación y suma son operaciones fundamentales'
      ],
      realWorldExample: 'En un sistema de construcción, necesitas calcular áreas (multiplicación) y perímetros (suma y multiplicación) para estimar materiales y costos.'
    },
    theory: `# Operadores en Cálculos Geométricos

## Fórmulas Geométricas

Las fórmulas matemáticas se traducen directamente a código usando operadores:

### Área de Rectángulo
\`\`\`python
area = base * altura
\`\`\`

### Perímetro de Rectángulo
\`\`\`python
perimetro = 2 * (base + altura)
\`\`\`

## Orden de Operaciones

Python sigue las reglas matemáticas estándar:

1. Paréntesis primero
2. Multiplicación/División
3. Suma/Resta

\`\`\`python
# Sin paréntesis: multiplicación primero
resultado = 2 * 3 + 4  # 10 (no 14)

# Con paréntesis: suma primero
resultado = 2 * (3 + 4)  # 14
\`\`\`

## Ejemplos Adicionales

### Área de círculo:
\`\`\`python
radio = 5
pi = 3.14159
area = pi * radio ** 2  # pi * r²
\`\`\`

### Volumen de cubo:
\`\`\`python
lado = 3
volumen = lado ** 3  # lado³
\`\`\``,
    tips: [
      'Usa paréntesis para clarificar el orden de operaciones',
      'Las fórmulas matemáticas se traducen directamente a código',
      '** es para potencia (elevar a una potencia)',
      'Puedes combinar múltiples operadores en una expresión'
    ],
    advice: [
      'Practica traduciendo fórmulas matemáticas a código',
      'Usa paréntesis cuando tengas dudas sobre el orden',
      'Verifica tus cálculos con ejemplos conocidos',
      'Los operadores son la base de todos los cálculos'
    ]
  },
  {
    id: 12,
    title: 'Operadores y expresiones - Ejercicio 3',
    description: 'Usa operadores de comparación y lógicos para verificar condiciones sobre números.',
    initialCode: `# Operadores de comparación y lógicos
numero = 15

# Verifica si el número está entre 10 y 20 (inclusive)
esta_en_rango = 

# Verifica si el número es par
es_par = 

# Verifica si el número es positivo Y par
es_positivo_y_par = 

print("¿Está en rango 10-20?:", esta_en_rango)
print("¿Es par?:", es_par)
print("¿Es positivo y par?:", es_positivo_y_par)`,
    solution: `# Operadores de comparación y lógicos
numero = 15

# Verifica si el número está entre 10 y 20 (inclusive)
esta_en_rango = numero >= 10 and numero <= 20

# Verifica si el número es par
es_par = numero % 2 == 0

# Verifica si el número es positivo Y par
es_positivo_y_par = numero > 0 and numero % 2 == 0

print("¿Está en rango 10-20?:", esta_en_rango)
print("¿Es par?:", es_par)
print("¿Es positivo y par?:", es_positivo_y_par)`,
    testCases: [],
    hints: [
      '>= significa mayor o igual',
      '<= significa menor o igual',
      'and combina condiciones',
      '% 2 == 0 verifica si es par',
      '> 0 verifica si es positivo'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué combinar operadores de comparación y lógicos?',
      content: `Combinar operadores de comparación y lógicos te permite crear condiciones complejas. Esto es esencial para validar datos y tomar decisiones en tu código.

### ¿Por qué este ejercicio?
- **Validación de datos**: Verificar rangos y condiciones es común
- **Lógica compleja**: Combina múltiples condiciones
- **Aplicación práctica**: Es exactamente lo que haces al validar entrada de usuario

### En el mundo real:
Cuando un sistema valida que una edad está en un rango válido (18-100) Y que el usuario aceptó los términos, usa operadores de comparación y lógicos combinados.`,
      keyPoints: [
        'and requiere que ambas condiciones sean True',
        '>= y <= verifican rangos inclusivos',
        '% 2 == 0 verifica si un número es par',
        'Puedes combinar múltiples condiciones con and/or'
      ],
      realWorldExample: 'En un sistema de registro, validas que la edad >= 18 AND que el email contiene "@" AND que la contraseña tiene más de 8 caracteres.'
    },
    theory: `# Combinando Operadores de Comparación y Lógicos

## Operadores de Comparación

Verifican relaciones entre valores:

\`\`\`python
numero >= 10  # Mayor o igual a 10
numero <= 20  # Menor o igual a 20
numero % 2 == 0  # El resto de dividir entre 2 es 0 (par)
numero > 0  # Mayor que 0 (positivo)
\`\`\`

## Operadores Lógicos

Combinan condiciones booleanas:

\`\`\`python
# and: ambas condiciones deben ser True
condicion1 and condicion2

# or: al menos una condición debe ser True
condicion1 or condicion2

# not: invierte el resultado
not condicion
\`\`\`

## Verificar Rangos

Para verificar si un número está en un rango:

\`\`\`python
# Entre 10 y 20 (inclusive)
numero >= 10 and numero <= 20

# Fuera del rango 10-20
numero < 10 or numero > 20
\`\`\`

## Verificar Paridad

El operador módulo (%) es útil para verificar propiedades:

\`\`\`python
# Es par: resto de dividir entre 2 es 0
numero % 2 == 0

# Es impar: resto de dividir entre 2 es 1
numero % 2 == 1
\`\`\`

## Combinar Condiciones

Puedes combinar múltiples condiciones:

\`\`\`python
# Positivo Y par
numero > 0 and numero % 2 == 0

# En rango O es negativo
(numero >= 10 and numero <= 20) or numero < 0
\`\`\`

## Precedencia

Los operadores lógicos tienen precedencia:
1. not (más alta)
2. and
3. or (más baja)

Usa paréntesis para clarificar:
\`\`\`python
# Sin paréntesis: and primero
True or False and False  # True

# Con paréntesis: or primero
(True or False) and False  # False
\`\`\``,
    tips: [
      'and requiere que ambas condiciones sean True',
      'or requiere que al menos una sea True',
      '>= y <= incluyen el valor límite',
      '% 2 == 0 verifica si es par',
      'Usa paréntesis para clarificar el orden'
    ],
    advice: [
      'Practica combinando diferentes condiciones',
      'Verifica tus condiciones con diferentes valores',
      'Usa paréntesis cuando combines and/or',
      'Piensa en qué condiciones necesitas verificar'
    ]
  },
  // Ejercicios adicionales para Print y salida
  {
    id: 13,
    title: 'Print y salida - Ejercicio 2',
    description: 'Formatea y muestra información de un producto usando print() con diferentes formatos.',
    initialCode: `# Formatea información de producto
nombre = "Laptop"
precio = 999.99
descuento = 0.15

# Calcula el precio con descuento
precio_final = precio * (1 - descuento)

# Muestra la información formateada
print()
print()
print()`,
    solution: `# Formatea información de producto
nombre = "Laptop"
precio = 999.99
descuento = 0.15

# Calcula el precio con descuento
precio_final = precio * (1 - descuento)

# Muestra la información formateada
print("Producto:", nombre)
print("Precio original: $", precio)
print("Precio con descuento: $", precio_final)`,
    testCases: [],
    hints: [
      'Puedes imprimir texto y variables juntos',
      'Separa con comas para que se impriman en la misma línea',
      'Puedes hacer cálculos dentro de print()',
      'Usa mensajes descriptivos'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué formatear la salida con print()?',
      content: `Formatear la salida hace que la información sea legible y profesional. En aplicaciones reales, la presentación de datos es tan importante como los cálculos.

### ¿Por qué este ejercicio?
- **Presentación profesional**: Muestra información de forma clara
- **Combinación de datos**: Imprime texto y variables juntos
- **Aplicación práctica**: Es lo que haces en sistemas de facturación

### En el mundo real:
Cuando un sistema muestra un recibo, necesita formatear precios, descuentos y totales de manera clara. Sin print() bien formateado, la información sería confusa.`,
      keyPoints: [
        'Puedes imprimir texto y variables en el mismo print()',
        'Los mensajes descriptivos ayudan a entender la salida',
        'Puedes hacer cálculos antes de imprimir',
        'El formato afecta la legibilidad'
      ],
      realWorldExample: 'En un sistema de facturación, print() muestra productos, precios, descuentos y totales de forma organizada y legible.'
    },
    theory: `# Formateo de Salida con print()

## Combinar Texto y Variables

Puedes imprimir texto y variables juntos:

\`\`\`python
nombre = "Laptop"
precio = 999.99
print("Producto:", nombre, "Precio: $", precio)
\`\`\`

## Múltiples Valores

print() acepta múltiples valores separados por comas:

\`\`\`python
print("Valor 1:", valor1, "Valor 2:", valor2)
\`\`\`

## Cálculos en print()

Puedes hacer cálculos directamente:

\`\`\`python
precio = 100
descuento = 0.2
print("Precio final:", precio * (1 - descuento))
\`\`\`

## Mensajes Descriptivos

Usa mensajes claros:

\`\`\`python
# Bueno: descriptivo
print("Precio original: $", precio)

# Malo: poco claro
print(precio)
\`\`\``,
    tips: [
      'Combina texto y variables para mensajes claros',
      'Usa mensajes descriptivos',
      'Puedes hacer cálculos dentro de print()',
      'Separa valores con comas',
      'Cada print() termina con salto de línea'
    ],
    advice: [
      'Haz que tus mensajes sean claros y descriptivos',
      'Practica combinando texto y variables',
      'Considera el formato para mejor legibilidad',
      'Los mensajes claros ayudan a entender el código'
    ]
  },
  // Ejercicios adicionales para Condicionales
  {
    id: 14,
    title: 'Condicionales - Ejercicio 2',
    description: 'Crea un sistema de descuentos basado en el monto de compra usando condicionales.',
    initialCode: `# Sistema de descuentos
monto_compra = 150

# Si el monto es mayor a 200, descuento del 20%
# Si el monto es mayor a 100, descuento del 10%
# Si no, sin descuento
if monto_compra > 200:
    descuento = 0.20
elif monto_compra > 100:
    descuento = 
else:
    descuento = 

precio_final = monto_compra * (1 - descuento)
print("Monto original: $", monto_compra)
print("Descuento:", descuento * 100, "%")
print("Precio final: $", precio_final)`,
    solution: `# Sistema de descuentos
monto_compra = 150

# Si el monto es mayor a 200, descuento del 20%
# Si el monto es mayor a 100, descuento del 10%
# Si no, sin descuento
if monto_compra > 200:
    descuento = 0.20
elif monto_compra > 100:
    descuento = 0.10
else:
    descuento = 0.0

precio_final = monto_compra * (1 - descuento)
print("Monto original: $", monto_compra)
print("Descuento:", descuento * 100, "%")
print("Precio final: $", precio_final)`,
    testCases: [],
    hints: [
      'elif verifica otra condición si la anterior fue False',
      'else maneja todos los demás casos',
      'Las condiciones se evalúan en orden',
      'Solo se ejecuta el primer bloque cuya condición sea True'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar condicionales para descuentos?',
      content: `Los sistemas de descuentos son un ejemplo perfecto de cuándo usar condicionales. Diferentes condiciones requieren diferentes acciones.

### ¿Por qué este ejercicio?
- **Lógica de negocio**: Implementa reglas de negocio comunes
- **Múltiples condiciones**: Practica elif para varios casos
- **Aplicación real**: Es exactamente lo que hacen los e-commerce

### En el mundo real:
Cuando compras en línea, el sistema verifica tu monto de compra y aplica descuentos automáticamente. Esto requiere condicionales para decidir qué descuento aplicar.`,
      keyPoints: [
        'elif permite múltiples condiciones en secuencia',
        'Las condiciones se evalúan de arriba hacia abajo',
        'Solo se ejecuta el primer bloque cuya condición sea True',
        'else maneja todos los casos que no cumplen las condiciones anteriores'
      ],
      realWorldExample: 'En un e-commerce, si compras más de $200 tienes 20% descuento, si compras más de $100 tienes 10%, y si no, precio normal.'
    },
    theory: `# Condicionales con Múltiples Condiciones

## Estructura if-elif-else

Cuando tienes múltiples condiciones posibles:

\`\`\`python
if condicion1:
    # Código si condicion1 es True
elif condicion2:
    # Código si condicion1 es False pero condicion2 es True
else:
    # Código si ninguna condición es True
\`\`\`

## Orden de Evaluación

Python evalúa las condiciones en orden:

1. Primero verifica \`if\`
2. Si es False, verifica \`elif\`
3. Si todos son False, ejecuta \`else\`
4. Solo ejecuta el primer bloque cuya condición sea True

## Ejemplo: Sistema de Descuentos

\`\`\`python
monto = 150

if monto > 200:
    descuento = 0.20  # 20% descuento
elif monto > 100:
    descuento = 0.10  # 10% descuento
else:
    descuento = 0.0   # Sin descuento
\`\`\`

## Importante sobre elif

- Puedes tener múltiples \`elif\`
- Solo se ejecuta el primer bloque cuya condición sea True
- Una vez que una condición es True, no se evalúan las siguientes

## Casos de Uso Comunes

- Sistemas de descuentos
- Clasificación de datos (A, B, C)
- Validación de entrada
- Niveles de acceso`,
    tips: [
      'elif verifica otra condición si la anterior fue False',
      'Las condiciones se evalúan en orden',
      'Solo se ejecuta el primer bloque cuya condición sea True',
      'else es opcional pero útil para casos por defecto',
      'Puedes tener múltiples elif'
    ],
    advice: [
      'Ordena las condiciones de más específica a más general',
      'Usa elif cuando tengas múltiples opciones mutuamente excluyentes',
      'Siempre prueba todos los casos posibles',
      'Considera si necesitas else para casos por defecto'
    ]
  },
  // Ejercicios adicionales para Ciclos
  {
    id: 15,
    title: 'Ciclos - Ejercicio 2',
    description: 'Usa un ciclo for para calcular la suma de los primeros 10 números naturales.',
    initialCode: `# Suma de los primeros 10 números
# Inicializa la suma en 0
suma = 0

# Usa un ciclo for para sumar del 1 al 10
for i in range():
    suma = suma + i

print("Suma de 1 a 10:", suma)`,
    solution: `# Suma de los primeros 10 números
# Inicializa la suma en 0
suma = 0

# Usa un ciclo for para sumar del 1 al 10
for i in range(1, 11):
    suma = suma + i

print("Suma de 1 a 10:", suma)`,
    testCases: [],
    hints: [
      'range(1, 11) genera números del 1 al 10',
      'Inicializa suma en 0 antes del ciclo',
      'Dentro del ciclo, suma el valor actual a suma',
      'Este es un patrón común de acumulación'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar ciclos para acumular valores?',
      content: `Acumular valores es uno de los usos más comunes de los ciclos. Te permite sumar, contar, o procesar múltiples elementos de forma eficiente.

### ¿Por qué este ejercicio?
- **Patrón común**: Acumulación es fundamental en programación
- **Eficiencia**: Hace el cálculo automáticamente sin repetir código
- **Escalabilidad**: Funciona igual con 10 o 10,000 números

### En el mundo real:
Cuando un sistema calcula el total de una compra, suma cada precio usando un ciclo. Sin ciclos, tendrías que escribir código para cada producto individualmente.`,
      keyPoints: [
        'Inicializa la variable acumuladora antes del ciclo',
        'Dentro del ciclo, actualiza el acumulador',
        'range(1, 11) genera números del 1 al 10',
        'Este patrón funciona para sumar, contar, multiplicar, etc.'
      ],
      realWorldExample: 'En un carrito de compras, un ciclo suma el precio de cada producto para calcular el total. Sin ciclos, esto sería muy difícil.'
    },
    theory: `# Acumulación con Ciclos

## Patrón de Acumulación

Un patrón común es acumular valores en un ciclo:

\`\`\`python
# 1. Inicializar acumulador
suma = 0

# 2. Iterar sobre valores
for i in range(1, 11):
    # 3. Actualizar acumulador
    suma = suma + i
\`\`\`

## Pasos del Patrón

1. **Inicializar**: Crea variable con valor inicial (0 para suma)
2. **Iterar**: Usa ciclo para procesar cada valor
3. **Acumular**: Actualiza la variable en cada iteración

## Otros Ejemplos de Acumulación

### Multiplicación (factorial):
\`\`\`python
factorial = 1
for i in range(1, 6):
    factorial = factorial * i
\`\`\`

### Contar elementos:
\`\`\`python
contador = 0
for numero in [1, 2, 3, 4, 5]:
    if numero % 2 == 0:
        contador = contador + 1
\`\`\`

## range() para Secuencias

\`range(inicio, fin)\` genera números desde inicio hasta fin-1:

\`\`\`python
range(1, 11)  # 1, 2, 3, ..., 10
range(0, 5)   # 0, 1, 2, 3, 4
\`\`\`

## Ventajas

- **Automático**: No necesitas escribir cada operación
- **Escalable**: Funciona con cualquier cantidad de elementos
- **Mantenible**: Cambias el rango, no el código de acumulación`,
    tips: [
      'Inicializa el acumulador antes del ciclo',
      'range(1, 11) genera números del 1 al 10',
      'Actualiza el acumulador dentro del ciclo',
      'Este patrón funciona para sumar, multiplicar, contar, etc.',
      'Puedes usar += como atajo: suma += i'
    ],
    advice: [
      'Practica el patrón de acumulación con diferentes operaciones',
      'Inicializa siempre el acumulador con el valor neutro de la operación',
      'Verifica que el rango sea correcto (inicio, fin)',
      'Este patrón es fundamental en programación'
    ]
  },
  // Ejercicios adicionales para Números
  {
    id: 16,
    title: 'Números y operaciones matemáticas - Ejercicio 2',
    description: 'Calcula el promedio de tres calificaciones y determina si el estudiante aprobó.',
    initialCode: `# Calcula promedio y verifica aprobación
calificacion1 = 85
calificacion2 = 90
calificacion3 = 78

# Calcula el promedio (suma / cantidad)
promedio = 

# Verifica si aprobó (promedio >= 70)
aprobo = promedio >= 70

print("Calificaciones:", calificacion1, calificacion2, calificacion3)
print("Promedio:", promedio)
print("¿Aprobó?:", aprobo)`,
    solution: `# Calcula promedio y verifica aprobación
calificacion1 = 85
calificacion2 = 90
calificacion3 = 78

# Calcula el promedio (suma / cantidad)
promedio = (calificacion1 + calificacion2 + calificacion3) / 3

# Verifica si aprobó (promedio >= 70)
aprobo = promedio >= 70

print("Calificaciones:", calificacion1, calificacion2, calificacion3)
print("Promedio:", promedio)
print("¿Aprobó?:", aprobo)`,
    testCases: [],
    hints: [
      'Suma las tres calificaciones',
      'Divide la suma entre 3',
      'Los paréntesis controlan el orden de operaciones',
      '>= verifica si es mayor o igual',
      'La división / siempre da float'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué calcular promedios con operaciones matemáticas?',
      content: `Calcular promedios es una operación matemática fundamental. Aprender a hacerlo correctamente te permite procesar datos y tomar decisiones basadas en cálculos.

### ¿Por qué este ejercicio?
- **Operación común**: Los promedios se usan en muchos contextos
- **Orden de operaciones**: Practicas el uso de paréntesis
- **Aplicación práctica**: Es lo que hacen los sistemas de calificaciones

### En el mundo real:
Cuando un sistema calcula el promedio de ventas mensuales, calificaciones de estudiantes, o ratings de productos, usa exactamente estas operaciones. Sin operaciones matemáticas, no podrías hacer estos cálculos.`,
      keyPoints: [
        'El promedio es suma / cantidad',
        'Los paréntesis aseguran que la suma se haga primero',
        'La división / siempre da float',
        'Puedes comparar el resultado con operadores de comparación'
      ],
      realWorldExample: 'En un sistema de calificaciones, calculas el promedio sumando todas las notas y dividiendo entre la cantidad, luego verificas si es >= 70 para aprobar.'
    },
    theory: `# Cálculo de Promedios

## Fórmula del Promedio

El promedio es la suma de valores dividida entre la cantidad:

\`\`\`python
promedio = (valor1 + valor2 + valor3) / cantidad
\`\`\`

## Orden de Operaciones

Los paréntesis son importantes:

\`\`\`python
# Correcto: suma primero, luego divide
promedio = (85 + 90 + 78) / 3  # 84.33

# Incorrecto: divide primero (resultado diferente)
promedio = 85 + 90 + 78 / 3  # 198.0 (incorrecto)
\`\`\`

## División y Tipos

La división / siempre devuelve float:

\`\`\`python
promedio = (85 + 90 + 78) / 3  # 84.333...
type(promedio)  # <class 'float'>
\`\`\`

## Comparación con Resultados

Puedes comparar el resultado:

\`\`\`python
promedio = (85 + 90 + 78) / 3
aprobo = promedio >= 70  # True o False
\`\`\`

## Ejemplos Adicionales

### Promedio de precios:
\`\`\`python
precios = [10.5, 20.0, 15.75]
promedio = sum(precios) / len(precios)
\`\`\`

### Redondear promedio:
\`\`\`python
promedio = (85 + 90 + 78) / 3
promedio_redondeado = round(promedio, 2)  # 84.33
\`\`\``,
    tips: [
      'Usa paréntesis para asegurar que la suma se haga primero',
      'La división / siempre da float',
      'Puedes comparar el resultado directamente',
      'El promedio es suma / cantidad',
      'Puedes usar round() para limitar decimales'
    ],
    advice: [
      'Siempre usa paréntesis cuando sumes antes de dividir',
      'Verifica tus cálculos con ejemplos conocidos',
      'Considera redondear el resultado si es necesario',
      'Los promedios son fundamentales en análisis de datos'
    ]
  },
  // Ejercicios adicionales para Strings
  {
    id: 17,
    title: 'Strings - Ejercicio 2',
    description: 'Procesa un texto: cuenta caracteres, convierte a mayúsculas, y extrae una subcadena.',
    initialCode: `# Procesa un texto
texto = "Python es genial"

# Cuenta cuántos caracteres tiene
longitud = 

# Convierte a mayúsculas
mayusculas = 

# Extrae la palabra "Python" (primeros 6 caracteres)
palabra = 

print("Texto original:", texto)
print("Longitud:", longitud)
print("Mayúsculas:", mayusculas)
print("Primera palabra:", palabra)`,
    solution: `# Procesa un texto
texto = "Python es genial"

# Cuenta cuántos caracteres tiene
longitud = len(texto)

# Convierte a mayúsculas
mayusculas = texto.upper()

# Extrae la palabra "Python" (primeros 6 caracteres)
palabra = texto[0:6]

print("Texto original:", texto)
print("Longitud:", longitud)
print("Mayúsculas:", mayusculas)
print("Primera palabra:", palabra)`,
    testCases: [],
    hints: [
      'len() cuenta caracteres',
      'upper() convierte a mayúsculas',
      'texto[0:6] obtiene caracteres del 0 al 5',
      'Los índices empiezan en 0',
      'El índice final no se incluye'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué procesar texto con métodos de string?',
      content: `Procesar texto es fundamental en programación. Los métodos de string te permiten manipular, analizar y transformar texto de forma eficiente.

### ¿Por qué este ejercicio?
- **Manipulación de texto**: Es común en aplicaciones
- **Métodos útiles**: len(), upper(), slicing son fundamentales
- **Aplicación práctica**: Procesar entrada de usuario, datos, etc.

### En el mundo real:
Cuando un sistema procesa nombres de usuarios, necesita contar caracteres (validación), convertir a mayúsculas (normalización), y extraer partes del texto (parsing). Todo esto requiere métodos de string.`,
      keyPoints: [
        'len() cuenta la cantidad de caracteres',
        'upper() devuelve un nuevo string en mayúsculas',
        'Slicing [inicio:fin] extrae subcadenas',
        'Los métodos de string devuelven nuevos strings (inmutabilidad)'
      ],
      realWorldExample: 'En un sistema de usuarios, procesas nombres: cuentas caracteres para validar longitud, conviertes a mayúsculas para consistencia, y extraes iniciales para mostrar.'
    },
    theory: `# Procesamiento de Strings

## Métodos Comunes de String

### len(): Longitud
\`\`\`python
texto = "Python"
longitud = len(texto)  # 6
\`\`\`

### upper(): Mayúsculas
\`\`\`python
texto = "python"
mayusculas = texto.upper()  # "PYTHON"
\`\`\`

### Slicing: Extraer Subcadenas
\`\`\`python
texto = "Python es genial"
palabra = texto[0:6]  # "Python" (índices 0-5)
\`\`\`

## Inmutabilidad

Los strings son inmutables: los métodos devuelven nuevos strings:

\`\`\`python
texto = "python"
mayusculas = texto.upper()  # Nuevo string
# texto sigue siendo "python" (no cambió)
\`\`\`

## Slicing Detallado

\`texto[inicio:fin]\` extrae del índice inicio al fin-1:

\`\`\`python
texto = "Python es genial"
texto[0:6]   # "Python" (índices 0-5)
texto[7:9]   # "es" (índices 7-8)
texto[10:]   # "genial" (del índice 10 al final)
\`\`\`

## Combinar Operaciones

Puedes combinar múltiples operaciones:

\`\`\`python
texto = "  Python  "
limpio = texto.strip().upper()  # "PYTHON"
\`\`\``,
    tips: [
      'len() cuenta caracteres (incluye espacios)',
      'upper() devuelve nuevo string, no modifica el original',
      'Slicing [inicio:fin] no incluye el índice fin',
      'Los índices empiezan en 0',
      'Puedes encadenar métodos'
    ],
    advice: [
      'Practica con diferentes métodos de string',
      'Recuerda que los strings son inmutables',
      'Usa slicing para extraer partes específicas',
      'Combina métodos para procesar texto complejo'
    ]
  },
  // Ejercicios adicionales para Listas
  {
    id: 18,
    title: 'Listas básicas - Ejercicio 2',
    description: 'Crea una lista de números, encuentra el máximo y mínimo, y calcula la suma total.',
    initialCode: `# Trabaja con lista de números
numeros = [5, 12, 8, 3, 15, 9]

# Encuentra el número máximo
maximo = 

# Encuentra el número mínimo
minimo = 

# Calcula la suma total
suma_total = 

print("Lista:", numeros)
print("Máximo:", maximo)
print("Mínimo:", minimo)
print("Suma total:", suma_total)`,
    solution: `# Trabaja con lista de números
numeros = [5, 12, 8, 3, 15, 9]

# Encuentra el número máximo
maximo = max(numeros)

# Encuentra el número mínimo
minimo = min(numeros)

# Calcula la suma total
suma_total = sum(numeros)

print("Lista:", numeros)
print("Máximo:", maximo)
print("Mínimo:", minimo)
print("Suma total:", suma_total)`,
    testCases: [],
    hints: [
      'max() encuentra el valor máximo',
      'min() encuentra el valor mínimo',
      'sum() suma todos los elementos',
      'Estas funciones funcionan con listas de números',
      'Puedes usar estas funciones directamente con la lista'
    ],
    validateOnlyCode: true,
    why: {
      title: '¿Por qué usar funciones built-in con listas?',
      content: `Python tiene funciones built-in muy útiles para trabajar con listas. Aprender a usarlas te hace más productivo y tu código más legible.

### ¿Por qué este ejercicio?
- **Funciones útiles**: max(), min(), sum() son muy comunes
- **Eficiencia**: Más rápido que escribir código manual
- **Legibilidad**: Código más claro y fácil de entender

### En el mundo real:
Cuando un sistema analiza ventas, necesita encontrar el día con más ventas (max), el día con menos ventas (min), y el total de ventas (sum). Estas funciones hacen esto fácilmente.`,
      keyPoints: [
        'max() encuentra el valor máximo en una lista',
        'min() encuentra el valor mínimo en una lista',
        'sum() suma todos los elementos numéricos',
        'Estas funciones son built-in de Python',
        'Son más eficientes que hacerlo manualmente'
      ],
      realWorldExample: 'En un sistema de análisis de ventas, usas max() para el mejor día, min() para el peor día, y sum() para el total mensual.'
    },
    theory: `# Funciones Built-in para Listas

## Funciones Útiles

Python tiene funciones built-in muy útiles para listas:

### max(): Valor Máximo
\`\`\`python
numeros = [5, 12, 8, 3, 15, 9]
maximo = max(numeros)  # 15
\`\`\`

### min(): Valor Mínimo
\`\`\`python
numeros = [5, 12, 8, 3, 15, 9]
minimo = min(numeros)  # 3
\`\`\`

### sum(): Suma Total
\`\`\`python
numeros = [5, 12, 8, 3, 15, 9]
suma_total = sum(numeros)  # 52
\`\`\`

## Ventajas de Funciones Built-in

- **Eficiencia**: Optimizadas en C, más rápidas
- **Legibilidad**: Código más claro
- **Confiabilidad**: Probadas y sin bugs
- **Menos código**: No necesitas escribir la lógica

## Comparación: Manual vs Built-in

### Manual (ineficiente):
\`\`\`python
maximo = numeros[0]
for num in numeros:
    if num > maximo:
        maximo = num
\`\`\`

### Built-in (eficiente):
\`\`\`python
maximo = max(numeros)
\`\`\`

## Otras Funciones Útiles

### len(): Longitud
\`\`\`python
longitud = len(numeros)  # 6
\`\`\`

### sorted(): Ordenar (devuelve nueva lista)
\`\`\`python
ordenados = sorted(numeros)  # [3, 5, 8, 9, 12, 15]
\`\`\``,
    tips: [
      'max() y min() funcionan con listas de números',
      'sum() suma todos los elementos numéricos',
      'Estas funciones son más eficientes que hacerlo manualmente',
      'Puedes usar estas funciones directamente con la lista',
      'len() también es muy útil para listas'
    ],
    advice: [
      'Usa funciones built-in cuando estén disponibles',
      'Son más eficientes y legibles que código manual',
      'Aprende qué funciones built-in existen',
      'Practica usando estas funciones con diferentes listas'
    ]
  }
];

